import { useState } from "react";
import { ArrowLeft, Check, Download, TrendingDown, Sparkles } from "lucide-react";
import RomEditor from "./RomEditor";
import SessionImpactCard from "./SessionImpactCard";
import StickyFooterCTA from "../ui/StickyFooterCTA";
import { api } from "../../lib/api";
import { toast } from "sonner";

function PainSlider({ label, testId, value, onChange, accent }) {
  return (
    <div>
      <div className="label-overline mb-3">{label}</div>
      <input
        data-testid={testId}
        type="range"
        min={0}
        max={10}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className={`w-full ${accent}`}
      />
      <div className="text-2xl font-heading text-ink-900 mt-2">{value}</div>
    </div>
  );
}

function PainDelta({ before, after }) {
  if (before == null || after == null) return null;
  const diff = before - after;
  if (Number(before) <= 0) return null;
  const pct = Math.round((diff / Number(before)) * 100);
  const pos = pct > 0;
  return (
    <div className="text-center mt-2" data-testid="pain-pct-improvement">
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${pos ? "bg-sage-500/10 text-sage-700" : pct === 0 ? "bg-paper-200 text-ink-600" : "bg-terracotta-500/10 text-terracotta-600"}`}>
        <TrendingDown className={`w-3.5 h-3.5 ${pos ? "" : "rotate-180"}`} />
        {pos ? `${pct}% pain reduction` : pct === 0 ? "Pain unchanged" : `${Math.abs(pct)}% increase`}
      </span>
    </div>
  );
}

function TechniqueChip({ label, active, onToggle, testId, variant = "sage" }) {
  const activeCls = variant === "sage"
    ? "bg-sage-500 text-white border-sage-500"
    : "bg-terracotta-500 text-white border-terracotta-500";
  const idleCls = variant === "sage"
    ? "bg-white border-paper-200 text-ink-700 hover:border-sage-500/40"
    : "bg-white border-terracotta-500/30 text-terracotta-600 hover:bg-terracotta-500/5";
  return (
    <button
      data-testid={testId}
      onClick={onToggle}
      className={`min-h-[40px] px-4 rounded-full border text-sm transition-all ${active ? activeCls : idleCls}`}
    >
      {label}
    </button>
  );
}

export default function OutcomeSection({
  outcome,
  setOutcome,
  analysis,
  user,
  billing,
  setBilling,
  protocols,
  selectedProtocolId,
  setSelectedProtocolId,
  duration,
  setDuration,
  save,
  saving,
  setPhase,
  sessionId,
  hideTopHeader = false,
  hidePainSliders = false,
  hideRomEditor = false,
}) {
  const toggleTechnique = (tech) => {
    const active = outcome.techniques_used.includes(tech);
    setOutcome({
      ...outcome,
      techniques_used: active
        ? outcome.techniques_used.filter((x) => x !== tech)
        : [...outcome.techniques_used, tech],
    });
  };

  const setRomMeasurements = (rom) => setOutcome({ ...outcome, rom_measurements: rom });
  const setClientReported = (list) => setOutcome({ ...outcome, client_reported_improvements: list });
  const onImpactUpdate = (s) => setOutcome({ ...outcome, impact_summary: s });

  // Iter AJ — "Generate Patient Summary" → one button kicks off impact-summary AI.
  // The fields above (improvements + findings) feed straight into the prompt; therapist
  // does not retype anything into a separate impact textarea.
  const [generatingImpact, setGeneratingImpact] = useState(false);
  const generatePatientSummary = async (force = false) => {
    if (!sessionId) {
      toast.error("Save the session first to generate the summary.");
      return;
    }
    setGeneratingImpact(true);
    try {
      const r = await api.post(`/sessions/${sessionId}/impact-summary`, { force_regenerate: force });
      onImpactUpdate(r.data);
      toast.success(force ? "Patient summary regenerated." : "Patient summary ready.");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) toast.error(e.response?.data?.detail || "AI quota reached.");
      else if (status === 503) toast.error("AI temporarily unavailable.");
      else toast.error("Couldn't generate patient summary.");
    } finally {
      setGeneratingImpact(false);
    }
  };

  const downloadProgressReport = async () => {
    if (!sessionId) {
      toast.error("Save the session first to download the progress report.");
      return;
    }
    try {
      const resp = await api.get(`/sessions/${sessionId}/progress-report.pdf`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `progress-report-${sessionId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Progress report downloaded.");
    } catch {
      toast.error("Couldn't download the progress report.");
    }
  };

  const userModalities = user?.profile?.modalities || [];
  const recommendedTechs = analysis?.recommended_techniques || [];

  return (
    <div data-testid="outcome-phase">
      {!hideTopHeader && (
        <>
          <div className="label-overline mb-2">Step 5 — Outcome</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-ink-900 mb-2">Track what worked</h1>
          <p className="text-ink-700 mb-8 max-w-xl">This data powers your pattern insights — which protocols deliver the best outcomes across clients.</p>
        </>
      )}

      <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 mb-6">
        {!hidePainSliders && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <PainSlider
                label="Pain before (0–10)"
                testId="pain-before-slider"
                value={outcome.pain_before}
                onChange={(v) => setOutcome({ ...outcome, pain_before: v })}
                accent="accent-terracotta-500"
              />
              <PainSlider
                label="Pain after (0–10)"
                testId="pain-after-slider"
                value={outcome.pain_after}
                onChange={(v) => setOutcome({ ...outcome, pain_after: v })}
                accent="accent-sage-500"
              />
            </div>
            <PainDelta before={outcome.pain_before} after={outcome.pain_after} />
          </>
        )}

        {!hideRomEditor && (
          <div className={hidePainSliders ? "" : "mt-8"}>
            <RomEditor value={outcome.rom_measurements || []} onChange={setRomMeasurements} />
          </div>
        )}

        <div className={hidePainSliders && hideRomEditor ? "" : "mt-8"}>
          <div className="label-overline mb-3">Session effectiveness</div>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                data-testid={`eff-${n}`}
                onClick={() => setOutcome({ ...outcome, effectiveness: n })}
                className={`min-h-[48px] flex-1 rounded-xl border transition-all font-medium ${outcome.effectiveness === n ? "bg-terracotta-500 text-white border-terracotta-500" : "bg-paper-50 border-paper-200 text-ink-700 hover:border-sage-500/40"}`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8">
          <div className="label-overline mb-3">Techniques used (drives &ldquo;what&rsquo;s working&rdquo; insights)</div>
          <div className="flex flex-wrap gap-2">
            {recommendedTechs.map((t) => (
              <TechniqueChip
                key={t.technique}
                label={t.technique}
                active={outcome.techniques_used.includes(t.technique)}
                onToggle={() => toggleTechnique(t.technique)}
                testId={`tech-chip-${t.technique}`}
                variant="sage"
              />
            ))}
            {recommendedTechs.length === 0 && (
              <div className="text-sm text-ink-500">No techniques suggested — add any below.</div>
            )}
          </div>
          {userModalities.length > 0 && (
            <>
              <div className="label-overline mt-5 mb-3 text-terracotta-600">My modalities (tap to log)</div>
              <div className="flex flex-wrap gap-2" data-testid="modality-quick-picks">
                {userModalities.map((m) => (
                  <TechniqueChip
                    key={m}
                    label={m}
                    active={outcome.techniques_used.includes(m)}
                    onToggle={() => toggleTechnique(m)}
                    testId={`modality-chip-${m}`}
                    variant="terracotta"
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="mt-8">
          <div className="label-overline mb-3">Client feedback</div>
          <textarea
            data-testid="client-feedback"
            value={outcome.client_feedback}
            onChange={(e) => setOutcome({ ...outcome, client_feedback: e.target.value })}
            rows={3}
            className="w-full px-4 py-3 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none resize-y"
            placeholder='"Felt great release in upper back. Plans to do stretches daily."'
          />
        </div>

        {/* Iter AJ — "What Changed Today?" — single card combining what the therapist
            documents (Improvements + Findings) with a single AI generate CTA. The
            "Session Impact Summary" textarea has been removed: the AI now writes it
            using the structured fields above, eliminating duplicate documentation. */}
        <div className="mt-8 bg-paper-50 border border-paper-200 rounded-3xl p-5 sm:p-7" data-testid="what-changed-today">
          <div className="label-overline mb-1 text-sage-700">Results</div>
          <h3 className="font-heading text-2xl text-ink-900 mb-1">What changed today?</h3>
          <p className="text-sm text-ink-600 mb-5 max-w-xl">
            Capture what your client said. The AI combines it with the clinical findings you recorded right after treatment to write the patient summary, portal content, and SOAP language — no retyping.
          </p>

          <div className="space-y-5">
            <div>
              <div className="label-overline mb-2">Client reported improvements (one per line)</div>
              <textarea
                data-testid="client-reported-improvements"
                value={(outcome.client_reported_improvements || []).join("\n")}
                onChange={(e) => setClientReported(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
                rows={3}
                className="w-full px-4 py-3 rounded-2xl bg-white border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none resize-y text-sm"
                placeholder={"Sleeping better\nCan turn head fully to check blind spot\nLess tension after long drives"}
              />
            </div>

            {!outcome.impact_summary ? (
              <button
                type="button"
                onClick={() => generatePatientSummary(false)}
                disabled={generatingImpact || !sessionId}
                data-testid="generate-patient-summary-btn"
                className="w-full sm:w-auto min-h-[48px] px-6 rounded-full bg-ink-900 hover:bg-ink-800 text-white font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Sparkles className="w-4 h-4" />
                {generatingImpact ? "Generating patient summary…" : "Generate patient summary"}
              </button>
            ) : (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5 text-xs text-sage-700 bg-sage-100 px-2.5 py-1 rounded-full">
                  <Check className="w-3 h-3" /> Summary generated
                </span>
                <button
                  type="button"
                  onClick={() => generatePatientSummary(true)}
                  disabled={generatingImpact}
                  data-testid="regenerate-patient-summary-btn"
                  className="text-xs text-ink-700 hover:text-ink-900 underline-offset-2 hover:underline disabled:opacity-50"
                >
                  {generatingImpact ? "Regenerating…" : "Regenerate"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Iter AJ — Session Impact Summary (AI-generated read-only display) */}
      {outcome.impact_summary && (
        <div className="mb-6">
          <SessionImpactCard
            sessionId={sessionId}
            impact={outcome.impact_summary}
            onUpdate={onImpactUpdate}
          />
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={downloadProgressReport}
              data-testid="download-progress-report-btn"
              className="min-h-[44px] px-5 rounded-full bg-ink-900 hover:bg-ink-700 text-white text-sm font-medium inline-flex items-center gap-2"
            >
              <Download className="w-4 h-4" /> Download progress report
            </button>
          </div>
        </div>
      )}

      {/* Billing / LOP block — Iter AJ moved Duration here since duration drives billable units */}
      <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8 mb-6">
        <div className="label-overline mb-4">Session billing &amp; case file</div>
        <p className="text-xs text-ink-500 mb-5">Duration is required for billing. Case/LOP fields only when working under an attorney or insurance claim.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label-overline block mb-2">Duration (minutes)</label>
            <input
              data-testid="duration-input"
              type="number"
              min={0}
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value) || 0)}
              className="w-full min-h-[44px] px-4 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
            />
          </div>
          <div>
            <label className="label-overline block mb-2">Case / LOP / Claim #</label>
            <input
              data-testid="case-ref-input"
              value={billing.case_reference}
              onChange={(e) => setBilling({ ...billing, case_reference: e.target.value })}
              className="w-full min-h-[44px] px-4 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
              placeholder="LOP-2025-0042"
            />
          </div>
          <div>
            <label className="label-overline block mb-2">Date of injury</label>
            <input
              data-testid="date-injury-input"
              type="date"
              value={billing.date_of_injury}
              onChange={(e) => setBilling({ ...billing, date_of_injury: e.target.value })}
              className="w-full min-h-[44px] px-4 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
            />
          </div>
          <div>
            <label className="label-overline block mb-2">CPT codes (comma-sep)</label>
            <input
              data-testid="cpt-input"
              value={billing.cpt_codes.join(", ")}
              onChange={(e) => setBilling({ ...billing, cpt_codes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              className="w-full min-h-[44px] px-4 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
              placeholder="97124, 97140, 97112"
            />
          </div>
          <div>
            <label className="label-overline block mb-2">ICD-10 codes (comma-sep)</label>
            <input
              data-testid="icd-input"
              value={billing.icd10_codes.join(", ")}
              onChange={(e) => setBilling({ ...billing, icd10_codes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              className="w-full min-h-[44px] px-4 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
              placeholder="S13.4XXA, M62.838"
            />
          </div>
        </div>
        {protocols.length > 0 && (
          <div className="mt-5">
            <label className="label-overline block mb-2">Protocol applied (optional)</label>
            <select
              data-testid="protocol-select"
              value={selectedProtocolId || ""}
              onChange={(e) => setSelectedProtocolId(e.target.value || null)}
              className="w-full min-h-[44px] px-4 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none"
            >
              <option value="">— None —</option>
              {protocols.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      <StickyFooterCTA testid="outcome-footer" className="justify-between">
        <button onClick={() => setPhase("soap")} className="min-h-[48px] px-5 rounded-full text-ink-700 hover:bg-paper-100 inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          data-testid="save-session-btn"
          onClick={save}
          disabled={saving}
          className="min-h-[48px] px-8 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium inline-flex items-center gap-2 disabled:opacity-50 shadow-sm hover:shadow-md transition-all"
        >
          {saving ? "Saving…" : "Save session"} <Check className="w-4 h-4" />
        </button>
      </StickyFooterCTA>
    </div>
  );
}
